import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useGetExhibitionQuery } from '../../../store/api/adminApi';
import PageLoader from '../../../components/PageLoader';
import { formatDateOnly, formatDateTime } from '../../../utils/helpers';
import { useLanguage } from '../../../context/LanguageContext';

const AdminExhibitionView: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { id } = useParams<{ id: string }>();
  const { data, isLoading, error } = useGetExhibitionQuery(id!);

  if (isLoading) {
    return <PageLoader />;
  }

  if (error || !data?.data) {
    return <div className="alert alert-danger m-5">{t('Exhibition not found')}</div>;
  }

  const exhibition = data.data;

  return (
    <div className="app-main flex-column flex-row-fluid" id="kt_app_main">
      <div className="d-flex flex-column flex-column-fluid">
        <div id="kt_app_toolbar" className="app-toolbar py-3 py-lg-6">
          <div id="kt_app_toolbar_container" className="app-container container-xxl d-flex flex-stack">
            <div className="page-title d-flex flex-column justify-content-center flex-wrap me-3">
              <h1 className="page-heading d-flex text-dark fw-bold fs-3 flex-column justify-content-center my-0">{t('Exhibition Details')}</h1>
              <ul className="breadcrumb breadcrumb-separatorless fw-semibold fs-7 my-0 pt-1">
                <li className="breadcrumb-item text-muted">
                  <a href="#" onClick={(e) => { e.preventDefault(); navigate('/admin/dashboard'); }} className="text-muted text-hover-primary">{t('Home')}</a>
                </li>
                <li className="breadcrumb-item">
                  <span className="bullet bg-gray-400 w-5px h-2px"></span>
                </li>
                <li className="breadcrumb-item text-muted">
                  <a href="#" onClick={(e) => { e.preventDefault(); navigate('/admin/exhibitions'); }} className="text-muted text-hover-primary">{t('Exhibitions')}</a>
                </li>
              </ul>
            </div>
            <div className="d-flex align-items-center gap-2 gap-lg-3">
              <button className="btn btn-primary" onClick={() => navigate(`/admin/exhibitions/${id}/edit`)}>
                {t('Edit Exhibition')}
              </button>
            </div>
          </div>
        </div>
        
        <div id="kt_app_content" className="app-content flex-column-fluid">
          <div id="kt_app_content_container" className="app-container container-xxl">
            <div className="card">
              <div className="card-body pt-0">
                <div className="row g-9 my-3">
                  <div className="col-md-6">
                    <label className="fs-6 fw-semibold mb-2">{t('Title')}</label>
                    <div className="form-control form-control-solid">{exhibition.title}</div>
                  </div>
                  <div className="col-md-6">
                    <label className="fs-6 fw-semibold mb-2">{t('Title (Arabic)')}</label>
                    <div className="form-control form-control-solid">{exhibition.title_ar || t('N/A')}</div>
                  </div>
                  <div className="col-md-6">
                    <label className="fs-6 fw-semibold mb-2">{t('Country')}</label>
                    <div className="form-control form-control-solid">{exhibition.country || t('N/A')}</div>
                  </div>
                  <div className="col-md-6">
                    <label className="fs-6 fw-semibold mb-2">{t('Status')}</label>
                    <div>
                      <span className={`badge badge-light-${exhibition.status === 1 ? 'success' : 'danger'}`}>
                        {exhibition.status === 1 ? t('Active') : t('Inactive')}
                      </span>
                    </div>
                  </div>
                  {exhibition.start_date && (
                    <div className="col-md-6">
                      <label className="fs-6 fw-semibold mb-2">{t('Start Date')}</label>
                      <div className="form-control form-control-solid">{formatDateOnly(exhibition.start_date)}</div>
                    </div>
                  )}
                  {exhibition.end_date && (
                    <div className="col-md-6">
                      <label className="fs-6 fw-semibold mb-2">{t('End Date')}</label>
                      <div className="form-control form-control-solid">{formatDateOnly(exhibition.end_date)}</div>
                    </div>
                  )}
                  {exhibition.image && (
                    <div className="col-md-12">
                      <label className="fs-6 fw-semibold mb-2">{t('Image')}</label>
                      <div>
                        <img src={`${process.env.REACT_APP_API_URL?.replace('/api', '')}/uploads/${exhibition.image}`} alt={exhibition.title} className="w-300px h-300px object-cover rounded" />
                      </div>
                    </div>
                  )}
                  {exhibition.description && (
                    <div className="col-md-12">
                      <label className="fs-6 fw-semibold mb-2">{t('Description')}</label>
                      <div className="form-control form-control-solid" style={{ minHeight: '100px' }}>{exhibition.description}</div>
                    </div>
                  )}
                  {exhibition.description_ar && (
                    <div className="col-md-12">
                      <label className="fs-6 fw-semibold mb-2">{t('Description (Arabic)')}</label>
                      <div className="form-control form-control-solid" style={{ minHeight: '100px' }}>{exhibition.description_ar}</div>
                    </div>
                  )}
                </div>
                
                <div className="card-footer d-flex justify-content-end py-6 px-9">
                  <button type="button" className="btn btn-light btn-active-light-primary me-2" onClick={() => navigate('/admin/exhibitions')}>
                    {t('Back')}
                  </button>
                  <button type="button" className="btn btn-primary" onClick={() => navigate(`/admin/exhibitions/${id}/edit`)}>
                    {t('Edit Exhibition')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminExhibitionView;

